import type { FunctionComponent, Ref } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { TextareaProps } from '~/ui/core/textarea/Textarea';
import { PromptComposerInput } from '~/ui/domain/PromptComposer/PromptComposer';

import { RecordingOverlay } from '../../components/transcription/RecordingOverlay';
import { useVoiceTranscription } from '../../components/transcription/use-voice-transcription';
import { TranscribeButton } from './TranscribeButton';

type TranscribableTextareaProps = Omit<TextareaProps, 'className' | 'onChange' | 'value' | 'variant'> & {
    readonly ref?: Ref<HTMLTextAreaElement>;
    readonly textareaClassName?: string;
    readonly value: string;
    readonly onValueChange: (value: string) => void;
};

export const TranscribableTextarea: FunctionComponent<TranscribableTextareaProps> = ({
    disabled,
    onValueChange,
    ref,
    textareaClassName,
    value,
    ...textareaProps
}) => {
    const translate = useTranslate();
    const valueRef = useRef(value);

    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    const handleTranscriptionChunk = useCallback(
        (text: string): void => {
            const nextValue = valueRef.current + text;
            valueRef.current = nextValue;
            onValueChange(nextValue);
            requestAnimationFrame(() => {
                if (typeof ref === 'function') {
                    return;
                }
                ref?.current?.focus({ preventScroll: true });
            });
        },
        [onValueChange, ref],
    );
    const transcription = useVoiceTranscription(handleTranscriptionChunk);

    return (
        <PromptComposerInput
            {...textareaProps}
            ref={ref}
            actions={
                <TranscribeButton
                    disabled={disabled === true}
                    isRecording={transcription.isRecording}
                    isTranscribing={transcription.isTranscribing}
                    title={translate('common.start-audio-transcription')}
                    onClick={transcription.toggleRecording}
                />
            }
            disabled={disabled}
            recordingOverlay={
                transcription.showOverlay ? (
                    <RecordingOverlay
                        getAmplitude={transcription.getAmplitude}
                        isFadingOut={transcription.overlayFadingOut}
                        isTranscribing={transcription.isTranscribing}
                        recordingSeconds={transcription.recordingSeconds}
                        onStopRecording={transcription.stopRecording}
                    />
                ) : undefined
            }
            textareaClassName={textareaClassName}
            value={value}
            onChange={(event) => {
                onValueChange(event.target.value);
            }}
        />
    );
};
